import React, { useState, useEffect } from 'react';
import {
    IonButton,
    IonIcon,
    IonText,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonSpinner,
} from '@ionic/react';
import { checkmarkCircleOutline, closeCircleOutline, refreshOutline, timeOutline, alertCircleOutline } from 'ionicons/icons';
import Page from '../../../components/core/Page';
import { Customer } from '../../../models';
import CurrencyPrefix from "../../../components/financials/currency-prefix";

interface Props {
    customer: Customer | null;
    proformaId: string;
    proforma: any;
    proformaStatus?: {
        isPaid: boolean;
        isFulfilled?: boolean;
        fulfilmentFailed?: boolean;
        nextAction?: 'pending' | 'book' | 'home' | 'fulfilment_failed';
        canCheckAgain?: boolean;
        canBook?: boolean;
        message?: string;
        bookableProfilePackageId?: number | null;
        checkedAt: string;
        error?: boolean;
    };
    errorMessage?: string;
    isFetching: boolean;
    onCheckStatus: () => void;
    onBookNow?: () => void;
}

const RATE_LIMIT_SECONDS = 30;

export const PaymentReturnPage: React.FC<Props> = ({
    customer,
    proformaId,
    proforma,
    proformaStatus,
    isFetching,
    onCheckStatus,
    onBookNow,
    errorMessage,
}) => {
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleCheckClick = () => {
        if (countdown === 0 && !isFetching) {
            onCheckStatus();
            setCountdown(RATE_LIMIT_SECONDS);
        }
    };

    const isPaid = !!proformaStatus?.isPaid;
    const isError = !!proformaStatus?.error || !!errorMessage;
    const nextAction = proformaStatus?.nextAction;

    const getStatusIcon = () => {
        if (nextAction === 'fulfilment_failed') return alertCircleOutline;
        if (nextAction === 'home' || nextAction === 'book') return checkmarkCircleOutline;
        if (nextAction === 'pending') return timeOutline;
        if (isPaid) return checkmarkCircleOutline;
        if (isError) return closeCircleOutline;
        return timeOutline; // Checking/Initial
    };

    const getStatusColor = () => {
        if (nextAction === 'fulfilment_failed') return "warning";
        if (nextAction === 'home' || nextAction === 'book') return "success";
        if (nextAction === 'pending') return "warning";
        if (isPaid) return "success";
        if (isError) return "danger";
        return "medium"; // Initial loading
    };

    const getStatusTitle = () => {
        if (errorMessage) return "Error";
        if (nextAction === 'fulfilment_failed') return "Order needs assistance";
        if (nextAction === 'home' || nextAction === 'book') return "Payment Complete";
        if (nextAction === 'pending') return "Still Confirming Payment";
        if (isPaid) return "Payment Complete";
        if (isError) return "Verification Failed";
        return "Finalising Payment...";
    };

    const getStatusDescription = () => {
        if (errorMessage) return errorMessage;
        if (nextAction === 'fulfilment_failed') return proformaStatus?.message || "Payment received, but we could not complete this order automatically. The swim school has been notified and will assist you.";
        if (nextAction === 'home') return proformaStatus?.message || "Your purchase is confirmed.";
        if (nextAction === 'book') return proformaStatus?.message || "Payment complete. You can now make your booking.";
        if (nextAction === 'pending') return proformaStatus?.message || "We’re confirming your payment. This usually only takes a moment.";
        if (isPaid) return proformaStatus?.message || "Payment received. We’re finalising your order.";
        if (isError) return proformaStatus?.message || "We could not verify this payment yet. If you have already paid, try again shortly.";
        return "We’re confirming your payment. This usually only takes a moment.";
    };

    const showCheckAgain = !errorMessage && !!proformaStatus?.canCheckAgain;
    const canBook = !errorMessage && !!proformaStatus?.canBook && !!proformaStatus?.bookableProfilePackageId;
    const showBookNow = canBook && !!onBookNow;

    return (
        <Page
            title="Payment Status"
            showMenuButton={false}
            showBackButton={true}
            backRouterLink="/home"
            showLoading={isFetching}
        >
            <div className="ion-padding ion-text-center">
                <IonIcon
                    icon={getStatusIcon()}
                    color={getStatusColor()}
                    style={{ fontSize: '64px', marginBottom: '16px' }}
                />

                <IonText color={getStatusColor()}>
                    <h1>{getStatusTitle()}</h1>
                </IonText>

                <p className="ion-margin-bottom">
                    {getStatusDescription()}
                </p>

                {proforma && (
                    <IonCard className="ion-text-left ion-margin-bottom">
                        <IonCardHeader>
                            <IonCardTitle style={{ fontSize: '1.1rem' }}>Purchase Summary</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            {proforma.lines?.map((line: any) => {
                                const learner = customer?.profiles?.find(p => p.id === line.learnerId);
                                return (
                                    <div key={line.id} style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{line.description} (x{Math.floor(parseFloat(line.quantity))})</span>
                                            <span><CurrencyPrefix customer={customer} />{line.totalDisplay}</span>
                                        </div>
                                        {learner && (
                                            <span style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                                                Learner: {learner.firstName} {learner.lastName}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--ion-color-light-shade)', marginTop: '12px', paddingTop: '12px', fontWeight: 'bold' }}>
                                <span>Total</span>
                                <span><CurrencyPrefix customer={customer} />{proforma.totalDisplay}</span>
                            </div>
                        </IonCardContent>
                    </IonCard>
                )}

                {showCheckAgain && (
                    <div className="ion-margin-top">
                        <IonButton
                            expand="block"
                            onClick={handleCheckClick}
                            disabled={isFetching || countdown > 0}
                            color="primary"
                        >
                            {isFetching ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <>
                                    <IonIcon icon={refreshOutline} slot="start" />
                                    Check Again {countdown > 0 ? `(${countdown}s)` : ''}
                                </>
                            )}
                        </IonButton>
                        {countdown > 0 && (
                            <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                                You can check again in {countdown} seconds.
                            </p>
                        )}
                    </div>
                )}

                {showBookNow && (
                    <div className="ion-margin-top">
                        <IonButton expand="block" onClick={onBookNow} color="success">
                            Book Now
                        </IonButton>
                    </div>
                )}

                <IonButton routerLink="/home" routerDirection="root" expand="block" fill="clear" className="ion-margin-top">
                    Go Home
                </IonButton>
            </div>
        </Page>
    );
};
